import { TableGrid } from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { ReactNode, useMemo } from "react";
import { useInvoicesItemFormPageContext } from "@web-src/modules/invoices/pages/InvoicesItemFormPage/context/useInvoicesItemFormPageContext";
import { InvoiceLineItem } from "@jugl-web/rest-api";
import { ItemsFormTableFooter } from "../ItemsFormTableFooter";
import { ActionCell } from "./components/ActionCell";
import { NameCell } from "./components/NameCell";
import { AmountCell } from "./components/AmountCell";
import { QuantityCell } from "./components/QuantityCell";
import { PriceCell } from "./components/PriceCell/PriceCell";
import { CategoryCell } from "./components/CategoryCell";

interface IndexCellProps {
  index: number;
}

const IndexCell = ({ index }: IndexCellProps) => (
  <div className="flex h-full items-center justify-center font-medium">
    {index + 1}
  </div>
);

type ItemsFormTableProps = {
  children: ReactNode;
};

export const ItemsFormTable = ({ children }: ItemsFormTableProps) => {
  const { t } = useTranslations();
  const { itemsField } = useInvoicesItemFormPageContext();
  const fields = useMemo(() => itemsField.fields, [itemsField.fields]);
  const optionalField = (node: ReactNode, fieldRowIdx: number): ReactNode => {
    const isLastRow = fieldRowIdx === fields.length - 1;
    if (isLastRow && !fields[fields.length - 1]?.name) {
      return null;
    }
    return node;
  };
  return (
    <div>
      <div className="jugl__custom-scrollbar overflow-x-auto">
        <TableGrid<InvoiceLineItem>
          id="invoices-item-form-table"
          classNames={{
            wrapper: "w-full",
          }}
          variant="grid"
          data={fields}
          onReachEnd={() => undefined}
          columns={[
            {
              id: "index",
              title: "#",
              width: 45,
              content: (_, index) => <IndexCell index={index} />,
              headerClassName: "rounded-tl-xl",
            },
            {
              id: "item-service-name",
              title: t({
                defaultMessage: "Item/Service name",
                id: "invoices.item-service-name",
              }),
              grow: true,
              className: "p-0",
              content: (item, rowIdx) => (
                <NameCell tableItem={item} idx={rowIdx} />
              ),
            },
            {
              id: "category",
              title: t({
                defaultMessage: "Category",
                id: "invoices.category",
              }),
              width: 130,
              content: (item, rowIdx) => (
                <>
                  {optionalField(
                    <CategoryCell item={item} idx={rowIdx} />,
                    rowIdx
                  )}
                </>
              ),
            },
            {
              id: "price-unit",
              title: t({
                defaultMessage: "Price (unit)",
                id: "invoices.price-unit",
              }),
              width: 180,
              className: "p-0",
              content: (item, rowIdx) => (
                <>
                  {optionalField(
                    <PriceCell item={item} idx={rowIdx} />,
                    rowIdx
                  )}
                </>
              ),
            },
            {
              id: "qty",
              title: t({
                defaultMessage: "Qty.",
                id: "invoices.qty",
              }),
              width: 120,
              className: "p-0",
              content: (item, rowIdx) => (
                <>
                  {optionalField(
                    <QuantityCell item={item} idx={rowIdx} />,
                    rowIdx
                  )}
                </>
              ),
            },
            {
              id: "amount",
              title: t({
                defaultMessage: "Amount",
                id: "invoices.amount",
              }),
              width: 160,
              grow: true,
              content: (item, rowIdx) => (
                <>{optionalField(<AmountCell item={item} />, rowIdx)}</>
              ),
            },
            {
              id: "action",
              title: t({
                defaultMessage: "Action",
                id: "invoices.action",
              }),
              width: 100,
              content: (item, rowIdx) => (
                <>{optionalField(<ActionCell />, rowIdx)}</>
              ),
              className: "cursor-pointer",
              onCellClick: (item, rowIdx) => itemsField.remove(rowIdx),
              headerClassName: "rounded-tr-xl border-r-0",
            },
          ]}
        />
      </div>

      <div className="flex justify-between gap-4 text-dark-800">
        {children}
        <ItemsFormTableFooter />
      </div>
    </div>
  );
};
